import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  Tooltip,
  Slider,
  FormControlLabel,
} from '@mui/material';
import { useHelpContent } from '../../utils/HelpUtils';

interface SuggestTreeContextDialogProps {
  open: boolean;
  onClose: () => void;
  onProceed: (context: string, maxDepth: number) => void;
  fileNames: string[];
}

const SuggestTreeContextDialog: React.FC<SuggestTreeContextDialogProps> = ({
  open,
  onClose,
  onProceed,
  fileNames,
}) => {
  const [context, setContext] = useState('');
  const [maxDepth, setMaxDepth] = useState(1);
  const { getTooltipTextForState } = useHelpContent();

  const handleProceed = () => {
    onProceed(context.trim(), maxDepth);
  };

  const handleClose = () => {
    setContext('');
    setMaxDepth(1);
    onClose();
  };

  const isProceedDisabled = () => {
    return false; // Allow proceeding even without context
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Provide Context for Tree Generation</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Files:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {fileNames.map((fileName, index) => (
              <Chip key={index} label={fileName} variant="outlined" size="small" />
            ))}
          </Box>
        </Box>

        <Typography variant="body1" gutterBottom>
          <strong>Optional Context:</strong> Provide additional context to help the AI generate better category names and clustering.
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Examples of helpful context:
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            • <strong>Domain/Field:</strong> "This is a legal document about contract law"
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            • <strong>Purpose:</strong> "These are research papers for a literature review"
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            • <strong>Structure Preference:</strong> "Organize by chapters, then by topics"
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            • <strong>Key Themes:</strong> "Focus on technical specifications and implementation details"
          </Typography>
        </Box>

        <Tooltip title={getTooltipTextForState('suggest-tree-context', true)} placement="top">
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Context (Optional)"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Enter any additional context that might help with categorization..."
            helperText="This context will be provided to the AI to improve category naming and clustering"
            variant="outlined"
            sx={{ mb: 3 }}
          />
        </Tooltip>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Maximum Tree Depth: {maxDepth}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set the maximum number of levels deep the suggested tree can be. Higher values create more detailed hierarchies.
          </Typography>
          <Tooltip title={getTooltipTextForState('suggest-tree-max-depth', true)} placement="top">
            <Slider
              value={maxDepth}
              onChange={(_, value) => setMaxDepth(value as number)}
              min={1}
              max={6}
              step={1}
              marks
              valueLabelDisplay="auto"
              sx={{ mt: 2 }}
            />
          </Tooltip>
        </Box>
      </DialogContent>
      <DialogActions>
        <Tooltip title={getTooltipTextForState('cancel', true)} placement="top">
          <Button onClick={handleClose}>Cancel</Button>
        </Tooltip>
        <Tooltip title={getTooltipTextForState('suggest-tree-proceed', true)} placement="top">
          <Button 
            onClick={handleProceed} 
            variant="contained" 
            disabled={isProceedDisabled()}
          >
            Generate Tree
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default SuggestTreeContextDialog; 